<template>
  <v-container class="px-0 py-0 ma-0 pa-0 prompt-container">
    <div class="px-2 py-1 prompt-shell">
      <v-row class="ma-0 pa-0">
        <v-col cols="4" class="ma-0 px-2 pt-0 pb-1">
          <v-text-field v-model="name" @blur="onNameChange" density="compact" variant="outlined" label="Prompt name" />
        </v-col>
        <v-col cols="4" class="ma-0 px-2 pt-0 pb-1">
          <v-select v-if="targetList.length > 0" v-model="selectedTarget" @update:modelValue="onTargetChange"
            :items="targetList" item-title="name" :return-object="true" label="Target Model or Agent"
            :rules="[() => !!selectedTarget || 'Target is required.']" density="compact" variant="outlined" />
          <v-select v-else v-model="selectedTarget" :items="targetList" item-title="name" :return-object="true"
            label="Add models or agents to get started" density="compact" variant="outlined" />
        </v-col>
        <v-col cols="2" class="ma-0 px-2 pt-0 pb-1">
          <v-text-field v-model.number="requestTimeout" @blur="onRequestTimeoutChange" label="Timeout (s)" type="number"
            density="compact" variant="outlined" hint="Request timeout (seconds)" persistent-hint />
        </v-col>
        <v-col cols="2" class="ma-0 px-2 pt-0 pb-1">
          <v-text-field v-model.number="requestRetries" @blur="onRequestRetriesChange" label="Retries" type="number"
            density="compact" variant="outlined" hint="Provider retry count" persistent-hint />
        </v-col>
      </v-row>
      <v-row class="ma-0 pa-0">
        <v-col cols="5" class="ma-0 px-2 pt-0 pb-1">
          <v-select v-model="selectedTargetImport" :items="agentTargets" item-title="name" :return-object="true"
            label="Load prompt from agent..." density="compact" variant="outlined" @update:modelValue="importPrompt">
            <template v-slot:prepend>
              <v-btn color="secondary" icon="mdi-arrow-down-bold-outline" size="x-large" variant="text"
                density="compact" @mousedown="stopProp" @click="importPrompt"
                :disabled="selectedTargetImport == null" />
            </template>
          </v-select>
        </v-col>
        <v-col cols="5" class="ma-0 px-2 pt-0 pb-1">
          <v-select v-model="selectedTargetExport" :items="agentTargets" item-title="name" :return-object="true"
            label="Save prompt to agent..." density="compact" variant="outlined">
            <template v-slot:append>
              <v-btn color="secondary" icon="mdi-arrow-right-bold-outline" size="x-large" variant="text"
                density="compact" @mousedown="stopProp" @click="exportPrompt"
                :disabled="selectedTargetExport == null" />
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-row class="ma-0 pa-0">
        <v-col cols="3" class="ma-0 px-2 pt-1 pb-4">
          <v-tooltip :text="isConnected ? 'Events listener connected' : 'Events listener disconnected'">
            <template v-slot:activator="{ props }">
              <div class="connection-status">
                <span class="status-indicator" v-bind="props"
                  :class="isConnected ? 'connected' : 'disconnected'"></span>
              </div>
            </template>
          </v-tooltip>
        </v-col>
        <v-col cols="3" class="ma-0 px-2 pt-0 pb-4">
          <span v-if="waitingForAgent !== ''">{{ waitingForAgent }} responding&nbsp;
            <ChatBubble class="ml-4" />
          </span>
        </v-col>
        <v-col cols="6" class="ma-0 px-2 pt-0 pb-4 flex-column d-flex align-end">
          <v-switch v-model="enableStreaming" @update:modelValue="toggleStreaming" color="primary" density="compact"
            variant="inset" hide-details class="mr-4">
            <template v-slot:label>
              <span class="text-overline px-4">{{enableStreaming ? 'Streaming On' : 'Streaming Off'}}</span>
              <v-icon small>{{ enableStreaming ? 'mdi-fast-forward' : 'mdi-play' }}</v-icon>
            </template>
          </v-switch>
          <v-switch v-model="showToolEvents" @update:modelValue="toggleToolEvents" color="secondary-darken-1"
            density="compact" variant="inset" hide-details>
            <template v-slot:label>
              <span class="text-overline px-4">{{showToolEvents ? 'Hide' : 'Show'}} Tool Events</span>
              <v-icon small>{{ showToolEvents ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </template>
          </v-switch>
        </v-col>
      </v-row>

      <!-- UNIFIED MESSAGE LIST -->
      <div ref="chatArea" class="chat-area">
        <div v-for="(message, index) in context" :key="`msg-${index}`" class="message-item-wrapper mb-6"
          :style="{ 'z-index': context.length - index }">
          <div class="flip-container"
            :style="{ height: flipContainerHeights[index] ? flipContainerHeights[index] + 'px' : 'auto' }">
            <transition name="flip" mode="out-in" @after-enter="() => updateFlipContainerHeight(index)">
              <!-- EDITING VIEW -->
              <v-row v-if="editingMessageIndex === index" :key="`edit-${index}`"
                :ref="(el: any) => setViewRef(el, index, 'edit')" class="flip-card mt-0 mx-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card class="pa-4" variant="outlined" style="height: 100%;">
                    <v-select v-model="message.role" :items="index === 0 ? allRoles : userRole" label="Role"
                      density="compact" variant="outlined" class="mb-2" style="max-width: 200px;"></v-select>
                    <v-textarea v-model="message.content" @update:model-value="() => scheduleHeightUpdate(index)"
                      label="Content" auto-grow rows="3" variant="outlined"></v-textarea>
                    <v-card-actions>
                      <Button color="warning" :click="() => removePrompt(index)" icon="mdi-delete-outline"
                        tooltip="Delete Message" size="large" />
                      <v-spacer></v-spacer>
                      <v-btn @click="() => saveAndCloseEdit()" variant="tonal" color="primary">Done</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- DISPLAY VIEW -->
              <v-row v-else :key="`display-${index}`" :ref="(el: any) => setViewRef(el, index, 'display')"
                class="flip-card mt-0 mx-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card class="mb-0" density="compact" style="height: 100%;">
                    <v-card-title class="d-flex align-center py-2">
                      <v-btn icon variant="text" size="x-small" @click="() => startEditing(index)" title="Edit message"
                        class="mr-2">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <span>Role: {{ message.role }}</span>
                    </v-card-title>
                    <v-divider></v-divider>
                    <!-- Tool Events Panel for assistant messages -->
                    <v-expansion-panels
                      v-if="message.role === 'assistant' && message.toolEvents && message.toolEvents.length > 0 && showToolEvents"
                      density="compact" class="mb-3">
                      <v-expansion-panel bg-color="background">
                        <v-expansion-panel-title color="primary-darken-2">
                          <div class="tool-events-header">
                            <span>Tool Events ({{ message.toolEvents.length }})</span>
                          </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-expansion-panels density="compact">
                            <v-expansion-panel v-for="(event, eventIndex) in message.toolEvents" :key="eventIndex"
                              :class="['event-panel', getEventTypeClass(event.type)]" bg-color="background">
                              <v-expansion-panel-title color="secondary-darken-2">
                                <div class="event-header">
                                  <span :class="['event-badge', getEventTypeClass(event.type), 'mr-4']"
                                    v-if="event.type">{{ event.type }}</span>
                                  <span v-if="event.message" class="event-message">{{ event.message }}</span>
                                </div>
                              </v-expansion-panel-title>
                              <v-expansion-panel-text>
                                <div class="event-content">
                                  <vue-json-pretty v-if="eventDataIsJson(event.data).isJson"
                                    :data="eventDataIsJson(event.data).data" deep="4" theme="dark" />
                                  <pre v-else>{{ event.data }}</pre>
                                </div>
                              </v-expansion-panel-text>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <FormattingCard :content="message.content" @vue:updated="() => scheduleHeightUpdate(index)"
                      @contentUpdated="() => scheduleHeightUpdate(index)" :expanded="'true'" :title="''" />
                  </v-card>
                  <!-- Token usage display -->
                  <div v-if="message.role === 'assistant' && message.tokenUsage" class="token-usage-display mb-3">
                    <span class="token-usage-label">Tokens: </span>
                    <span class="token-usage-value">
                      Prompt: {{message.tokenUsage.promptTokens}} |
                      Completion: {{message.tokenUsage.completionTokens}} |
                      Total: {{message.tokenUsage.totalTokens}}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </transition>
          </div>
        </div>
      </div>

      <!-- Waiting for response -->
      <v-row class="mt-0 mx-0 mb-5 pa-0" v-if="waitingForAgent !== ''">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="mb-4" density="compact">
            <v-card-text>
              <span><span class="text-subtitle-1">{{ waitingForAgent }} responding</span>
                <ChatBubble class="ml-4" />
              </span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Image thumbnails row -->
      <v-row v-if="uploadedImages.length > 0" class="ma-0 pa-0 mb-2">
        <v-col cols="12" class="ma-0 pa-2">
          <div class="image-thumbnails-container">
            <div v-for="(image, index) in uploadedImages" :key="`img-${index}`" class="image-thumbnail-wrapper">
              <img :src="image.preview" :alt="image.fileName" class="image-thumbnail" />
              <v-btn icon="mdi-close-circle" size="x-large" class="delete-image-btn" @click="removeImage(index)"
                variant="text" color="warning" />
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Chat input -->
      <v-row class="ma-0 pa-0 chat-input-row">
        <v-col cols="12" class="ma-0 pa-0">
          <v-textarea v-model="chatInput" class="chat-input pt-1" variant="outlined" density="compact"
            label="Message..." @keydown.meta.enter="chat" :readonly="waitingForAgent !== ''" hide-details="auto">
            <template v-slot:prepend>
              <Button color="primary" :click="selectImages" icon="mdi-plus" size="large" tooltip="Add images"
                :disabled="waitingForAgent !== ''" />
            </template>
            <template v-slot:append>
              <div v-if="isStreaming" class="stream-stop-wrapper">
                <v-progress-circular color="primary" indeterminate size="32" width="3" />
                <v-btn
                  class="stream-stop-btn"
                  color="warning"
                  icon
                  size="x-small"
                  variant="flat"
                  @click="stopStreamingChat"
                >
                  <v-icon size="16">mdi-stop</v-icon>
                </v-btn>
              </div>
              <Button
                v-else
                color="primary"
                :click="chat"
                icon="mdi-send-outline"
                size="x-large"
                tooltip="Send (⌘ + Enter)"
                :disabled="!chatInput.trim() || waitingForAgent !== ''"
              />
            </template>
          </v-textarea>
        </v-col>
      </v-row>

      <!-- Hidden file input for image selection -->
      <input ref="hiddenFileInput" type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple
        style="display: none" @change="handleFileInputChange" />

      <!-- Add new message button -->
      <!-- <v-row class="ma-0 pa-0 mt-4">
        <v-col cols="12" class="d-flex justify-center">
          <v-btn @click="addPrompt" variant="outlined" prepend-icon="mdi-plus">
            Add Message
          </v-btn>
        </v-col>
      </v-row> -->
    </div>
  </v-container>
</template>
<script lang="ts">
import { Options, Vue, mixins } from 'vue-class-component'
import { mapGetters, mapState } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import { toRaw } from 'vue'
import { nanoid } from 'nanoid'
import { PromptInterfaceTarget, PromptStudioState, PromptConfig, PromptHistory } from '@/store/prompts'
import { parseText } from '@/utils/general'
import Button from '@/components/sub/Button.vue'
import FormattingCard from '@/components/cards/FormattingCard.vue'
import ChatBubble from '@/components/sub/ChatBubble.vue'
import { CompletionApiRequest, GenerateResult, ModelMessage, MessageContent, ToolEvent } from '@/types/msq'
import { ModelsState } from '@/store/models'
import { generateSessionId, connectSSE, addEventListener, removeEventListener, getState, clearEvents, updateSessionId, disconnectSSE } from '@/utils/sse'
import {
  cancelChatStream,
  startStreamingAgentCompletionRequest,
  startStreamingCompletionRequest,
  type StreamingRequestHandle
} from '@/utils/api'
import { createMessageContent, VALID_IMAGE_TYPES } from '@/utils/imageHandler'
import { AgentConfig, UserAgentConfig } from '@/types/agents'
import { ModelConfig } from '@/types/models'
import { ApiErrorResponse, isApiError } from '@/types/api'
import { debugLog } from '@/utils/debug'
import { PtcTranscriptFilter } from '@/utils/ptcSuppression'

type Target = AgentConfig | ModelConfig

function isModelConfig(target: Target): target is ModelConfig {
  return (target as AgentConfig).systemPrompt == null
}

@Options({
  props: {
    id: String
  },
  components: {
    Button,
    FormattingCard,
    ChatBubble
  },
  computed: {
    ...mapState(['models', 'prompts']),
    ...mapGetters(['modelConfigsList', 'agentConfigsList', 'username', 'promptConfigById']) // Add promptConfigById
  }
})
export default class Prompt extends mixins(AnalyticsMixin) {
  id!: string
  username!: string
  promptConfigById!: (id: string) => PromptConfig | undefined
  timestamp: number = 0
  selectedTarget: Target | null = null
  selectedTargetImport: AgentConfig | null = null
  selectedTargetExport: AgentConfig | null = null
  target?: PromptInterfaceTarget
  name: string = ''
  context: ModelMessage[] = []
  lastResponseTimestamp: number = 0
  temperature: number = 0.1
  maxTokens: number = -1
  history: PromptHistory[] = []
  models!: ModelsState
  prompts!: PromptStudioState
  modelConfigsList!: ModelConfig[]
  agentConfigsList!: UserAgentConfig[]
  allRoles: string[] = ['user', 'system']
  userRole: string[] = ['user']
  waitingForAgent: string = ''
  chatInput: string = ''
  parseText = parseText
  requestTimeout: number = 300; // Default to 300 seconds (5 minutes)
  requestRetries: number = 2;
  enableStreaming: boolean = true

  // Streaming state
  isStreaming: boolean = false
  streamingMessageIndex: number | null = null
  activeToolEventMessageIndex: number | null = null
  currentSessionId: string = ''
  activeStreamHandle: StreamingRequestHandle | null = null
  currentStreamRunId: string | null = null
  streamCancelledByUser: boolean = false
  private ptcFilter = new PtcTranscriptFilter()
  
  // SSE related properties
  isConnected: boolean = false
  eventListener: ((event: any) => void) | null = null
  showToolEvents: boolean = true
  pollInterval: number | null = null

  // In-place editing state
  editingMessageIndex: number | null = null
  flipContainerHeights: number[] = []
  viewRefs: { [key: string]: HTMLElement | null } = {} 
  heightUpdateTimers: { [key: number]: number } = {}

  // Image upload state
  uploadedImages: Array<{
    mimeType: string
    base64Data: string
    preview: string
    fileName: string
  }> = []
  hiddenFileInput: HTMLInputElement | null = null

  // Batch height update management
  batchHeightUpdateTimer: number | null = null
  pendingHeightUpdates: Set<number> = new Set()
  isMountedAndReady: boolean = false
  heightCalculationInProgress: boolean = false

  get targetList(): Target[] {
    return (<Target[]>[]).concat(this.modelConfigsList).concat(this.agentConfigsList.map((agent) => agent.config))
  }

  private filterAssistantTranscriptChunk(chunk: string): string {
    return this.ptcFilter.filterChunk(chunk)
  }

  private beginToolEventRouting(messageIndex: number): void {
    this.activeToolEventMessageIndex = messageIndex
  }

  private clearToolEventRouting(): void {
    this.activeToolEventMessageIndex = null
  }

  get agentTargets(): AgentConfig[] {
    if (this.agentConfigsList != null) {
      return this.agentConfigsList.map(agent => agent.config)
    }
    return []
  }

  eventDataIsJson(data: Object | string) {
    if (typeof data === 'object') {
      return {
        isJson: true,
        data
      }
    }
    try {
      const parsed = JSON.parse(data as string)
      return {
        isJson: true,
        data: parsed
      }
    } catch (e) {
      return {
        isJson: false,
        data
      }
    }
  }

  // Get appropriate class for event type
  getEventTypeClass(type: string): string {
    switch (type) {
      case 'tool_start':
        return 'tool_start'
      case 'tool_end':
        return 'tool_end'
      case 'error':
        return 'tool_error'
      case 'connected':
        return 'connected'
      case 'disconnected':
        return 'disconnected'
      default:
        return ''
    }
  }
  
  
  // SSE related methods
  setupSSE(): void {
    connectSSE(generateSessionId(this.username))

    this.eventListener = (event: any) => {
      // Only process events for the current session
      if (event.sessionId === this.currentSessionId && this.activeToolEventMessageIndex !== null) {
        const toolEvent: ToolEvent = {
          type: event.type,
          message: event.message,
          data: event.data,
          sessionId: event.sessionId,
          clientId: event.clientId,
          timestamp: Date.now()
        }
        
        // Add to the current streaming message's toolEvents
        const currentMessage = this.context[this.activeToolEventMessageIndex]
        if (!currentMessage) {
          return
        }
        if (!currentMessage.toolEvents) {
          currentMessage.toolEvents = []
        }
        currentMessage.toolEvents.push(toolEvent)

        this.trackEvent('tool_event_received', {
          tool_event_type: event.type,
          tool_name: event.data?.tool_name,
          tool_event_message: event.message,
          is_error: event.type === 'error'
        });
      }
    }
    addEventListener(this.eventListener)
    this.pollSseState()
  }

  pollSseState() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    
    this.pollInterval = setInterval(() => {
      if (!this.$el) { // Stop polling if component is destroyed
        if (this.pollInterval) clearInterval(this.pollInterval);
        return;
      }
      const sseState = getState();
      if (this.isConnected !== sseState.isConnected) {
        this.isConnected = sseState.isConnected;
      }
    }, 500); // Poll every 500ms
  }

  cleanupSSE(): void {
    if (this.eventListener) {
      removeEventListener(this.eventListener)
      this.eventListener = null
    }
    
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    disconnectSSE()
    this.isConnected = false
    this.clearToolEventRouting()
  }

  mounted() {
    this.updateData()
    this.setupSSE()
    this.initializeFlipContainerHeights()
    
    // Delay setting mounted flag to ensure DOM is stable
    requestAnimationFrame(() => {
      this.isMountedAndReady = true;
      this.$nextTick(() => {
        // Initial height calculation
        for (let i = 0; i < this.context.length; i++) {
          this.pendingHeightUpdates.add(i);
        }
        this.batchUpdateHeights();
        
        // Setup hidden file input ref
        this.hiddenFileInput = this.$refs.hiddenFileInput as HTMLInputElement
      });
    });
  }
  
  beforeUnmount() {
    this.isMountedAndReady = false;
    if (this.activeStreamHandle) {
      this.activeStreamHandle.controller.abort()
      this.activeStreamHandle = null
    }
    this.cleanupSSE()
    
    // Clear all timers
    Object.values(this.heightUpdateTimers).forEach(clearTimeout);
    if (this.batchHeightUpdateTimer) {
      clearTimeout(this.batchHeightUpdateTimer);
    }
    
    // Clear pending updates
    this.pendingHeightUpdates.clear();
  }
  
  updated() {
    const promptConfig = this.promptConfigById(this.id)
    if (promptConfig && promptConfig.name !== this.name) {
      this.updateData()
    }
    
    // Only update heights if mounted and ready
    if (this.isMountedAndReady) {
      // Ensure heights array matches context length
      if (this.context.length !== this.flipContainerHeights.length) {
        this.initializeFlipContainerHeights();
      }
      
      // Schedule height updates for all items
      this.$nextTick(() => {
        for (let i = 0; i < this.context.length; i++) {
          this.pendingHeightUpdates.add(i);
        }
        this.batchUpdateHeights();
      });
    }
  }

  initializeFlipContainerHeights() {
    this.flipContainerHeights = this.context.map(() => 150); // Default height, will be updated
  }

  setViewRef(el:  (Vue & { $el: HTMLElement | undefined }) | HTMLElement | null, index: number, type: 'edit' | 'display') {
    if (el) {
      // Check if el is a Vue component instance with an $el property that is an HTMLElement
      if ('$el' in el && el.$el instanceof HTMLElement) {
        this.viewRefs[`${type}-${index}`] = el.$el;
      // Check if el itself is an HTMLElement
      } else if (el instanceof HTMLElement) {
        this.viewRefs[`${type}-${index}`] = el;
      // Otherwise, the ref is not what we expect
      } else {
        this.viewRefs[`${type}-${index}`] = null;
      }
    } else {
      this.viewRefs[`${type}-${index}`] = null;
    }
  }

  scheduleHeightUpdate(index: number) {
    if (!this.isMountedAndReady) {
      return;
    }
    
    // Add to pending updates instead of immediate update
    this.pendingHeightUpdates.add(index);
    this.batchUpdateHeights();
  }

  updateFlipContainerHeight(index: number) {
    this.$nextTick(() => {
      const type = this.editingMessageIndex === index ? 'edit' : 'display';
      const viewRefKey = `${type}-${index}`;
      const viewElement = this.viewRefs[viewRefKey] as HTMLElement | null; // Cast to HTMLElement
      
      if (viewElement && typeof viewElement.offsetHeight === 'number') {
        const newHeight = viewElement.offsetHeight;
        if (this.flipContainerHeights[index] !== newHeight && newHeight > 0) {
           // Ensure Vue reacts to array changes correctly
          const newHeights = [...this.flipContainerHeights];
          newHeights[index] = newHeight;
          this.flipContainerHeights = newHeights;
        }
      } else if (this.editingMessageIndex === null && index < this.context.length) { 
        const displayViewRefKey = `display-${index}`;
        const displayViewElement = this.viewRefs[displayViewRefKey];
        if (displayViewElement && typeof displayViewElement.offsetHeight === 'number') {
          const newHeight = displayViewElement.offsetHeight;
           if (this.flipContainerHeights[index] !== newHeight && newHeight > 0) {
            const newHeights = [...this.flipContainerHeights];
            newHeights[index] = newHeight;
            this.flipContainerHeights = newHeights;
          }
        }
      }
    });
  }

  batchUpdateHeights(): void {
    if (this.batchHeightUpdateTimer) {
      clearTimeout(this.batchHeightUpdateTimer);
    }
    
    this.batchHeightUpdateTimer = window.setTimeout(() => {
      if (!this.isMountedAndReady || this.heightCalculationInProgress) {
        // Reschedule if not ready
        this.batchUpdateHeights();
        return;
      }
      
      this.heightCalculationInProgress = true;
      
      // Use requestAnimationFrame to ensure DOM has settled
      requestAnimationFrame(() => {
        const updates = Array.from(this.pendingHeightUpdates).sort((a, b) => a - b);
        this.pendingHeightUpdates.clear();
        
        updates.forEach(index => {
          if (index < this.context.length) {
            this.updateFlipContainerHeightSafe(index);
          }
        });
        
        this.heightCalculationInProgress = false;
        this.batchHeightUpdateTimer = null;
      });
    }, 100); // 100ms debounce
  }

  updateFlipContainerHeightSafe(index: number): void {
    if (!this.$el || !this.isMountedAndReady) {
      return;
    }
    
    const type = this.editingMessageIndex === index ? 'edit' : 'display';
    const viewRefKey = `${type}-${index}`;
    const viewElement = this.viewRefs[viewRefKey] as HTMLElement | null;
    
    if (viewElement && typeof viewElement.offsetHeight === 'number') {
      const newHeight = viewElement.offsetHeight;
      if (newHeight > 0 && this.flipContainerHeights[index] !== newHeight) {
        // Use Vue.set equivalent for reactivity
        const newHeights = [...this.flipContainerHeights];
        newHeights[index] = newHeight;
        this.flipContainerHeights = newHeights;
      }
    }
  }

  updateData() {
    const promptConfig = this.promptConfigById(this.id) // Use getter instead of direct access
    if (promptConfig != null) {
      // The getter now handles the migration, so we can simplify this
      const { timestamp, target, name, context, temperature, maxTokens, history } = promptConfig
      this.timestamp = timestamp
      this.target = target
      this.name = name
      this.context = context || []
      this.temperature = temperature
      this.maxTokens = maxTokens
      this.history = history ?? []
    }
    if (this.target != null) {
      const target = this.targetList.find((t) => t.id === this.target!.id)
      if (target != null) {
        this.selectedTarget = target
      }
    }
    if (this.context.length === 0) {
      this.addPrompt()
      this.startEditing(0)
    }
  }

  startEditing(index: number) {
    this.trackButtonClick('edit_message', 'start', { message_index: index, role: this.context[index].role });
    // Pre-set height to current display view before flip
    this.$nextTick(() => { // Ensure refs are available
      const displayViewRefKey = `display-${index}`;
      const displayViewElement = this.viewRefs[displayViewRefKey] as HTMLElement | null;
      if (displayViewElement && typeof displayViewElement.offsetHeight === 'number') {
        const currentHeight = displayViewElement.offsetHeight;
        if (currentHeight > 0 && (!this.flipContainerHeights[index] || this.flipContainerHeights[index] !== currentHeight)) {
          const newHeights = [...this.flipContainerHeights];
          newHeights[index] = currentHeight;
          this.flipContainerHeights = newHeights;
        }
      }
      this.editingMessageIndex = index;
      // updateFlipContainerHeight will be called by @after-enter on the transition
      // and scheduleHeightUpdate by content changes within the edit view
    });
  }

  saveAndCloseEdit() {
    this.trackButtonClick('edit_message', 'save', { message_index: this.editingMessageIndex });
    this.savePrompt();
    const currentIndex = this.editingMessageIndex;
    if (currentIndex !== null) {
      // Pre-set height to current edit view before flip
      this.$nextTick(() => { // Ensure refs are available
        const editViewRefKey = `edit-${currentIndex}`;
        const editViewElement = this.viewRefs[editViewRefKey] as HTMLElement | null;
        if (editViewElement && typeof editViewElement.offsetHeight === 'number') {
          const currentHeight = editViewElement.offsetHeight;
          if (currentHeight > 0 && (!this.flipContainerHeights[currentIndex] || this.flipContainerHeights[currentIndex] !== currentHeight)) {
            const newHeights = [...this.flipContainerHeights];
            newHeights[currentIndex] = currentHeight;
            this.flipContainerHeights = newHeights;
          }
        }
        this.editingMessageIndex = null;
        // updateFlipContainerHeight will be called by @after-enter on the transition
      });
    }
  }

  addPrompt() {
    this.context.push({ role: 'user', content: '' });
    this.$nextTick(() => {
      this.startEditing(this.context.length - 1);
    });
  }

  removePrompt(index: number) {
    this.trackButtonClick('delete_message', 'click', { message_index: index, role: this.context[index].role });
    
    // Pause height calculations
    const wasCalculating = this.heightCalculationInProgress;
    this.heightCalculationInProgress = true;
    
    this.context.splice(index, 1);
    // Adjust flipContainerHeights array
    const newHeights = [...this.flipContainerHeights];
    newHeights.splice(index, 1);
    this.flipContainerHeights = newHeights;

    // Clean up refs and timers for the removed item
    delete this.viewRefs[`edit-${index}`];
    delete this.viewRefs[`display-${index}`];
    if (this.heightUpdateTimers[index]) {
      clearTimeout(this.heightUpdateTimers[index]);
      delete this.heightUpdateTimers[index];
    }

    if (this.editingMessageIndex === index) {
      this.editingMessageIndex = null;
    } else if (this.editingMessageIndex !== null && this.editingMessageIndex > index) {
      this.editingMessageIndex--;
    }
    
    this.savePrompt();
    
    // Schedule height updates for remaining items
    requestAnimationFrame(() => {
      this.heightCalculationInProgress = wasCalculating;
      for (let i = 0; i < this.context.length; i++) {
        this.pendingHeightUpdates.add(i);
      }
      this.batchUpdateHeights();
    });
  }

  async importPrompt() {
    if (this.selectedTargetImport) {
      this.trackButtonClick('import_prompt_from_agent', 'click', { agent_id: this.selectedTargetImport.id });
    }
    if (this.selectedTargetImport == null) {
      return
    }
    
    // Pause height calculations during import
    const wasCalculating = this.heightCalculationInProgress;
    this.heightCalculationInProgress = true;
    
    // Clear any pending height updates
    this.pendingHeightUpdates.clear();
    if (this.batchHeightUpdateTimer) {
      clearTimeout(this.batchHeightUpdateTimer);
      this.batchHeightUpdateTimer = null;
    }
    
    // Find and remove existing system prompt
    const systemPromptIndex = this.context.findIndex((item) => item.role === 'system');
    if (systemPromptIndex !== -1) {
      this.context.splice(systemPromptIndex, 1);
    }

    const { systemPrompt } = this.selectedTargetImport;
    const prompt = { content: systemPrompt, role: 'system' };
    if ((this.context.length === 1 && this.context[0].content === '') || this.context.length === 0) {
      this.context = [prompt];
    } else if (this.context.length > 0) {
      this.context.unshift(prompt);
    }

    // Wait for Vue's DOM updates
    await this.$nextTick();
    
    // Schedule height updates for next animation frame
    requestAnimationFrame(() => {
      this.heightCalculationInProgress = wasCalculating;
      
      // Add all indices to pending updates
      for (let i = 0; i < this.context.length; i++) {
        this.pendingHeightUpdates.add(i);
      }
      
      // Trigger batch update
      this.batchUpdateHeights();
    });
  }

  async exportPrompt() {
    if (this.selectedTargetExport) {
      this.trackButtonClick('export_prompt_to_agent', 'click', { agent_id: this.selectedTargetExport.id });
    }
    if (this.selectedTargetExport == null) {
      return
    }
    const { id, timestamp, name, description, model, temperature, maxTokens, modelOptions, embeddingOptions, toolOptions, addToday, timezoneOffset } = this.selectedTargetExport
    
    // Correctly look up the model configuration by its ID
    let modelConfig = this.models.configsById[model]
    if (modelConfig == null) {
      modelConfig = this.models.configs[model] // Fallback to the old way of looking up model in case there's a bug or an old model config.
    }
    if (modelConfig == null) {
      this.$store.dispatch('updateSnackbar', { text: `Could not find model config for ID ${model}`, color: 'error' })
      return
    }

    const systemPrompt = this.context.find(m => m.role === 'system');
    if (!systemPrompt || typeof systemPrompt.content !== 'string') {
      this.$store.dispatch('updateSnackbar', { text: 'A system prompt with simple text content is required to export.', color: 'error' });
      return;
    }

    const config: AgentConfig = {
      id,
      timestamp,
      name,
      description,
      model: modelConfig.config.model, // Use the model's actual name for the API
      temperature,
      maxTokens,
      modelOptions,
      embeddingOptions,
      toolOptions,
      addToday,
      timezoneOffset,
      systemPrompt: systemPrompt.content,
      context: []
    }
    await this.$store.dispatch('setAgentConfig', config)
    this.$store.dispatch('updateSnackbar', { text: `Saved prompt to agent ${config.name}`, timeout: 2_000 })
  }

  stopProp(e: any) {
    e.stopPropagation()
  }

  get prompt(): PromptConfig | undefined {
    if (
      this.name === '' ||
      this.context.length === 0 ||
      (this.context.length > 0 && this.context[0].role === '') ||
      this.selectedTarget == null
    ) {
      return undefined
    }
    const targetType = isModelConfig(this.selectedTarget) ? 'model' : 'agent'
    this.target = {
      id: this.selectedTarget.id,
      type: targetType,
      timestamp: this.lastResponseTimestamp > this.timestamp ? this.lastResponseTimestamp : this.timestamp
    }
    
    return {
      id: this.id === '' ? nanoid() : this.id,
      timestamp: this.timestamp > 0 ? this.timestamp : Date.now(),
      name: this.name,
      target: this.target,
      context: this.context, // Pass the full context to be saved
      temperature: this.temperature,
      maxTokens: this.maxTokens
    }
  }

  savePrompt() {
    const prompt = this.prompt
    if (prompt != null) {
      // The prompt getter now returns the full context, which is what we want to save.
      // The store will handle compression, and the API functions will handle stripping for network requests.
      this.$store.dispatch('setPromptConfig', prompt)
      debugLog('Saved prompt:', prompt)
    }
  }

  // --- PROMPT CONFIGURATION ---
  onNameChange() {
    this.trackFormInteraction('prompt_config', 'update_field', 'prompt_name');
    this.savePrompt();
  }

  onTargetChange(selectedTarget: Target) {
    this.trackFeatureUsage('prompt', 'select_target', { 
      target_id: selectedTarget.id, 
      target_name: selectedTarget.name,
      target_type: isModelConfig(selectedTarget) ? 'model' : 'agent'
    });
  }

  onRequestTimeoutChange() {
    this.trackFormInteraction('prompt_config', 'update_field', 'request_timeout');
    if (!Number.isFinite(this.requestTimeout) || this.requestTimeout <= 0) {
      this.requestTimeout = 300
    } else {
      this.requestTimeout = Math.floor(this.requestTimeout)
    }
  }

  onRequestRetriesChange() {
    this.trackFormInteraction('prompt_config', 'update_field', 'request_retries');
    if (!Number.isFinite(this.requestRetries) || this.requestRetries < 0) {
      this.requestRetries = 2
    } else {
      this.requestRetries = Math.min(10, Math.floor(this.requestRetries))
    }
  }

  // --- CHAT CONTROLS ---
  toggleStreaming(enabled: boolean | null) {
    this.trackButtonClick('toggle_streaming', 'click', { enabled: !!enabled });
  }

  toggleToolEvents(shown: boolean | null) {
    this.trackButtonClick('toggle_tool_events', 'click', { shown: !!shown });
  }

  async chat() {
    this.trackButtonClick('send_chat_message', 'click', { 
      target_id: this.selectedTarget?.id,
      message_length: this.chatInput.length,
      image_count: this.uploadedImages.length,
      context_length: this.context.length
    });
    this.saveAndCloseEdit()
    if (this.isStreaming || this.waitingForAgent) {
      return
    }
    if (
      this.selectedTarget == null ||
      this.context.length === 0 ||
      (this.context.length > 0 && this.context[0].role === '')
    ) {
      this.$store.dispatch('updateSnackbar', {
        text: `You must selected a target, choose a role and enter a prompt.`,
        timeout: 3_000
      })
      return
    }
    
    this.showToolEvents = true
    
    if (this.context.length === 0) {
      this.context.push(...this.history.map((h) => ({ role: 'assistant', content: h.response })))
    }
    
    // Build message content using the new utility function
    const messageContent = createMessageContent(this.chatInput, this.uploadedImages)
    
    this.context.push({ role: 'user', content: messageContent })
    
    const sessionId = generateSessionId(this.username)
    updateSessionId(sessionId)
    this.currentSessionId = sessionId
    
    this.waitingForAgent = this.selectedTarget.name
    
    const messages = this.context
    this.savePrompt()
    this.chatInput = ''
    this.clearImages() // Clear images after adding to context
    this.$store.dispatch('updateSnackbar', { text: `Sent message to ${this.selectedTarget.name}`, timeout: 2_000 })
    this.scrollToBottom()

    if (this.enableStreaming) {
      await this.streamChat(messages)
    } else {
      await this.nonStreamChat(messages)
    }
  }

  async nonStreamChat(messages: ModelMessage[]) {
    try {
      let response: GenerateResult | ApiErrorResponse
      
      if (this.selectedTarget != null && (this.selectedTarget as AgentConfig).systemPrompt != null) {
        response = await this.$store.dispatch('promptAgent', {
          messages,
          name: this.selectedTarget.name,
          timeout: this.requestTimeout * 1000,
          requestTimeoutMs: this.requestTimeout * 1000,
          maxRetries: this.requestRetries
        })
      } else {
        const modelConfig = isModelConfig(this.selectedTarget!)
          ? this.selectedTarget!
          : this.models.configs[this.selectedTarget!.model].config
        const options = modelConfig.options ?? undefined
        
        response = await this.$store.dispatch('completionRequest', {
          messages,
          modelConfig,
          onRetry: (() => {
            this.$store.dispatch('updateSnackbar', { text: `Prompt studio request failed, retrying` })
          }).bind(this),
          options,
          timeout: this.requestTimeout * 1000,
          requestTimeoutMs: this.requestTimeout * 1000,
          maxRetries: this.requestRetries
        })
      }
      
      if (response != null) {
        if (isApiError(response)) {
          this.context.push({ 
            role: 'assistant', 
            content: `Error: ${response.message}`
          })
        } else {
          if (response.content != null) {
            const { content, usage } = response
            const role = 'assistant'
            const cleanResponse = content.replace(/\\"/g, '').trim()
            this.context.push({ 
              role, 
              content: cleanResponse,
              tokenUsage: usage
            })
          } else if (response.rawResponse != null
            && (response.rawResponse as any).choices != null 
            && Array.isArray((response.rawResponse as any).choices)
            && (response.rawResponse as any).choices.length > 0
          ) {
            const { role, content } = (response.rawResponse as any).choices[0].message ?? {}
            const cleanResponse = (content ?? '').replace(/\\"/g, '').trim()
            this.context.push({ 
              role, 
              content: cleanResponse,
              tokenUsage: response.usage
            })
          }
        }
      }
    } catch (error: any) {
      this.context.push({ 
        role: 'assistant', 
        content: `Error: ${error?.message || 'An unexpected error occurred.'}`
      })
    } finally {
      this.waitingForAgent = ''
      this.lastResponseTimestamp = Date.now()
      this.savePrompt()
      setTimeout(() => this.scrollToBottom(), 150)
    }
  }

  get currentRunId(): string | undefined {
    return this.currentStreamRunId ?? this.activeStreamHandle?.runId
  }

  finalizeStreamingCancellation() {
    this.isStreaming = false
    this.streamingMessageIndex = null
    this.waitingForAgent = ''
    this.activeStreamHandle = null
    this.currentStreamRunId = null
    this.lastResponseTimestamp = Date.now()
    this.ptcFilter.reset()
    this.savePrompt()
    this.scrollToBottom()
  }

  private wasServerCancellationConfirmed(
    response: { success?: boolean; found?: boolean; cancelled?: boolean; alreadyCancelled?: boolean } | ApiErrorResponse
  ): boolean {
    if (isApiError(response)) return false
    if (response.success !== true) return false

    const hasResultFlags =
      typeof response.cancelled === 'boolean'
      || typeof response.alreadyCancelled === 'boolean'
      || typeof response.found === 'boolean'

    if (!hasResultFlags) return true
    return response.cancelled === true || response.alreadyCancelled === true
  }

  async stopStreamingChat() {
    if (!this.isStreaming) return

    this.streamCancelledByUser = true
    const sessionId = this.currentSessionId
    const runId = this.currentRunId

    if (this.activeStreamHandle) {
      this.activeStreamHandle.controller.abort()
    }
    this.finalizeStreamingCancellation()

    if (!sessionId) return
    try {
      const response = await cancelChatStream(sessionId, runId)
      if (!this.wasServerCancellationConfirmed(response)) {
        console.warn('[Prompt] cancelChatStream did not confirm cancellation', { sessionId, runId, response })
      }
    } catch {
      console.warn('[Prompt] cancelChatStream threw unexpectedly', { sessionId, runId })
    }
  }

  async streamChat(messages: ModelMessage[]) {
    this.isStreaming = true
    this.streamingMessageIndex = null
    this.activeToolEventMessageIndex = null
    this.currentStreamRunId = null
    this.streamCancelledByUser = false
    this.ptcFilter.reset()

    const commonCallbacks = {
      onChunk: (chunk: string) => {
        this.ensureStreamingMessageExists()
        const visibleChunk = this.filterAssistantTranscriptChunk(chunk)
        if (this.streamingMessageIndex !== null) {
          this.context[this.streamingMessageIndex].content += visibleChunk
          this.scrollToBottom()
        }
      },
      onComplete: (finalContent: string, usage: any) => {
        this.trackEvent('chat_response_completed', {
          target_id: this.selectedTarget?.id,
          status: 'success',
          streaming: true,
          response_length: finalContent.length,
          total_tokens: usage?.totalTokens
        });
        if (this.streamingMessageIndex !== null) {
          this.context[this.streamingMessageIndex].tokenUsage = usage
        }
        this.activeStreamHandle = null
        this.currentStreamRunId = null
        this.streamCancelledByUser = false
        this.isStreaming = false
        this.streamingMessageIndex = null
        this.ptcFilter.reset()
        this.waitingForAgent = ''
        this.lastResponseTimestamp = Date.now()
        this.savePrompt()
        this.scrollToBottom()
      },
      onError: (error: Error) => {
        if (this.streamCancelledByUser) {
          this.finalizeStreamingCancellation()
          return
        }
        this.trackEvent('chat_response_completed', {
          target_id: this.selectedTarget?.id,
          status: 'failure',
          streaming: true,
          error_message: error.message
        });
        this.ensureStreamingMessageExists()
        if (this.streamingMessageIndex !== null) {
          this.context[this.streamingMessageIndex].content = `Error: ${error.message}`
        }
        this.activeStreamHandle = null
        this.currentStreamRunId = null
        this.isStreaming = false
        this.streamingMessageIndex = null
        this.waitingForAgent = ''
        this.savePrompt()
        this.scrollToBottom()
      },
      onCancel: () => {
        this.finalizeStreamingCancellation()
      },
      onMeta: (meta: { runId?: string }) => {
        if (meta.runId) {
          this.currentStreamRunId = meta.runId
        }
      }
    }

    try {
      if (this.selectedTarget != null && (this.selectedTarget as AgentConfig).systemPrompt != null) {
        this.activeStreamHandle = startStreamingAgentCompletionRequest({
          messages,
          name: this.selectedTarget.name,
          timeout: this.requestTimeout * 1000,
          requestTimeoutMs: this.requestTimeout * 1000,
          maxRetries: this.requestRetries,
          ...commonCallbacks
        })
        await this.activeStreamHandle.promise
      } else {
        const modelConfig = isModelConfig(this.selectedTarget!)
          ? this.selectedTarget!
          : this.models.configs[this.selectedTarget!.model].config
        const options = modelConfig.options ?? undefined
        
        this.activeStreamHandle = startStreamingCompletionRequest({
          messages,
          modelConfig,
          options,
          timeout: this.requestTimeout * 1000,
          requestTimeoutMs: this.requestTimeout * 1000,
          maxRetries: this.requestRetries,
          ...commonCallbacks
        })
        await this.activeStreamHandle.promise
      }
    } catch (error: any) {
      if (!this.streamCancelledByUser) {
        commonCallbacks.onError(error instanceof Error ? error : new Error('An unexpected error occurred.'))
      }
    } finally {
      this.activeStreamHandle = null
    }
  }

  ensureStreamingMessageExists() {
    if (this.streamingMessageIndex === null) {
      this.streamingMessageIndex = this.context.length;
      this.context.push({ role: 'assistant', content: '', toolEvents: [] });
      this.beginToolEventRouting(this.streamingMessageIndex)
      this.scrollToBottom();
    }
  }

  // Image upload methods
  selectImages(): void {
    this.trackButtonClick('add_images', 'click');
    if (this.hiddenFileInput) {
      this.hiddenFileInput.click()
    }
  }

  handleFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.handleImageUpload(Array.from(input.files))
    }
    // Reset input value to allow selecting the same file again
    input.value = ''
  }

  async handleImageUpload(files: File[]): Promise<void> {
    this.trackEvent('images_uploaded', { count: files.length });
    for (const file of files) {
      // Validate file type
      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        this.$store.dispatch('updateSnackbar', { 
          text: `Invalid file type: ${file.name}. Only JPEG, PNG, GIF, and WebP images are allowed.`, 
          color: 'error',
          timeout: 3000 
        })
        continue
      }
      
      // Optional: Add file size validation (e.g., 5MB limit)
      const maxSizeInBytes = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSizeInBytes) {
        this.$store.dispatch('updateSnackbar', { 
          text: `File too large: ${file.name}. Maximum size is 5MB.`, 
          color: 'error',
          timeout: 3000 
        })
        continue
      }
      
      try {
        const imageData = await this.readImageAsBase64(file)
        this.uploadedImages.push(imageData)
      } catch (error) {
        console.error('Error reading image:', error)
        this.$store.dispatch('updateSnackbar', { 
          text: `Failed to read image: ${file.name}`, 
          color: 'error',
          timeout: 3000 
        })
      }
    }
  }

  readImageAsBase64(file: File): Promise<{
    mimeType: string
    base64Data: string
    preview: string
    fileName: string
  }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = () => {
        const result = reader.result as string
        // Remove data URL prefix to get pure base64
        const base64Data = result.split(',')[1]
        
        resolve({
          mimeType: file.type,
          base64Data: base64Data,
          preview: result, // Keep full data URL for preview
          fileName: file.name
        })
      }
      
      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${file.name}`))
      }
      
      reader.readAsDataURL(file)
    })
  }

  removeImage(index: number): void {
    this.trackButtonClick('remove_image', 'click', { image_index: index });
    this.uploadedImages.splice(index, 1)
  }

  clearImages(): void {
    this.uploadedImages = []
  }

  scrollToBottom() {
    this.$nextTick(() => {
      const chatArea = this.$refs.chatArea as HTMLElement
      if (chatArea) {
        chatArea.scrollTop = chatArea.scrollHeight
      }
    })
  }
}
</script>
<style lang="scss" scoped>
.prompt-container {
  height: 100%;
}

.prompt-shell {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.chat-area {
  flex: 1 1 auto;
  min-height: 240px;
  max-height: none;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 104px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
}

.chat-area::-webkit-scrollbar { /* WebKit */
  width: 0;
  height: 0;
}

.image-thumbnails-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px;
  background-color: rgba(var(--v-theme-theme-code), 0.5);
  border-radius: 4px;
  max-height: 120px;
  overflow-y: auto;
}

.image-thumbnail-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
}

.image-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid rgba(var(--v-theme-primary), 0.3);
}

.delete-image-btn {
  position: absolute;
  top: -26px;
  right: -26px;
  // background-color: rgb(var(--v-theme-surface));
}

// Update chat input to accommodate the prepend button
.chat-input {
  background-color: rgb(var(--v-theme-background));
  :deep(.v-field__prepend-inner),
  :deep(.v-field__append-inner) {
    align-self: end;
    padding-bottom: 8px;
  }
}

.chat-input-row {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background-color: rgb(var(--v-theme-background));
  border-top: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.stream-stop-wrapper {
  position: relative;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stream-stop-btn {
  position: absolute;
  inset: 0;
  margin: auto;
  min-width: 20px !important;
  width: 20px !important;
  height: 20px !important;
}

.event {
  border-radius: 6px;
  background-color: rgba(var(--v-theme-background), 0.9);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  margin-bottom: 8px;
  color: rgb(var(--v-theme-secondary));
  
  &.tool_start {
    border-left: 4px solid rgb(var(--v-theme-info));
  }
  
  &.tool_end {
    border-left: 4px solid rgb(var(--v-theme-success));
  }
  
  &.tool_error {
    border-left: 4px solid rgb(var(--v-theme-error));
  }
  
  &.connected {
    border-left: 4px solid rgb(var(--v-theme-success));
  }
  
  &.disconnected {
    border-left: 4px solid rgb(var(--v-theme-error));
  }
}

.event-panel {
  margin-bottom: 8px;
  
  &.tool_start {
    border-left: 4px solid rgb(var(--v-theme-primary-darken-1));
  }
  
  &.tool_end {
    border-left: 4px solid rgb(var(--v-theme-success));
  }
  
  &.tool_error {
    border-left: 4px solid rgb(var(--v-theme-error));
  }
  
  &.connected {
    border-left: 4px solid rgb(var(--v-theme-success));
  }
  
  &.disconnected {
    border-left: 4px solid rgb(var(--v-theme-error));
  }
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  
  .event-type {
    font-weight: 600;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
  }
  
  .event-badge {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface)) !important;
    
    &.tool_start {
      background-color: rgb(var(--v-theme-secondary));
    }
    
    &.tool_end {
      background-color: rgb(var(--v-theme-success));
    }
    
    &.tool_error {
      background-color: rgb(var(--v-theme-error));
    }
  }
}

.event-content {
  padding: 10px 14px;
  background-color: rgb(var(--v-theme-theme-code));
  border-radius: 4px;
  
  .event-message {
    margin-bottom: 10px;
    font-size: 14px;
    color: rgb(var(--v-theme-secondary));
  }
  
  pre {
    white-space: pre-wrap;
    word-break: break-word;
    background-color: rgba(var(--v-theme-surface-variant), 0.8);
    padding: 12px;
    border-radius: 4px;
    font-size: 13px;
    border: 1px solid rgba(var(--v-theme-primary), 0.3);
    color: rgb(var(--v-theme-secondary));
    margin: 0;
    max-height: 300px;
    overflow-y: auto;
  }
}

.connection-status {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  
  .status-indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    
    &.connected {
      background-color: rgb(var(--v-theme-success));
    }
    
    &.disconnected {
      background-color: rgb(var(--v-theme-error));
    }
  }
}

.tool-events-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.token-usage-display {
  margin-top: -4px;
  padding: 4px 12px;
  font-size: 12px;
  color: rgba(var(--v-theme-primary), 0.8);
  text-align: right;
  
  .token-usage-label {
    font-weight: 500;
  }
  
  .token-usage-value {
    font-family: monospace;
  }
}

:deep(.vjs-tree.dark) {
  background-color: transparent !important;

  .vjs-tree__brackets,
  .vjs-tree__comma,
  .vjs-key + .vjs-colon + span > .vjs-tree__brackets,
  span.vjs-colon {
    color: rgb(var(--v-theme-theme-on-code)) !important;
  }
  &.vjs-tree span:not([class*="vjs-value"]):not(.vjs-key):not(.vjs-tree-brackets):not(.vjs-colon) {
    color: rgb(var(--v-theme-theme-on-code)) !important;
  }

  .vjs-key {
    color: rgb(var(--v-theme-secondary)) !important;
  }
  .vjs-value.vjs-value-string {
    color: rgb(var(--v-theme-success)) !important;
  }
  .vjs-value.vjs-value-number {
    color: rgb(var(--v-theme-warning)) !important;
  }
  .vjs-value.vjs-value-boolean {
    color: rgb(var(--v-theme-primary)) !important;
  }
  .vjs-value.vjs-value-null {
    color: rgba(var(--v-theme-theme-on-code), 0.7) !important;
  }

  .vjs-tree__node.dark {
    &:hover,
    &.vjs-tree__node--highlighted {
      background-color: rgba(var(--v-theme-primary), 0.2) !important;

      &, .vjs-key, .vjs-value, .vjs-tree__brackets, .vjs-colon, span {
        color: rgb(var(--v-theme-on-surface)) !important;
      }
    }
  }
}

@mixin panel-title-hover-fix($text-container-selector) {
  #{$text-container-selector},
  .v-expansion-panel-title__icon .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
    transition: color 0.1s ease-in-out, background-color 0.1s ease-in-out;
  }

  &:hover {
    span.v-expansion-panel-title__overlay {
      background-color: transparent !important;
      opacity: 0 !important;
    }
    background-color: rgba(var(--v-theme-on-surface), 0.08) !important;

    #{$text-container-selector},
    .v-expansion-panel-title__icon .v-icon {
      color: rgb(var(--v-theme-on-surface)) !important;
    }
  }

  &.v-expansion-panel-title--active {
    #{$text-container-selector},
    .v-expansion-panel-title__icon .v-icon {
      color: rgb(var(--v-theme-on-surface)) !important;
    }
    span.v-expansion-panel-title__overlay {
        background-color: transparent !important;
        opacity: 0 !important;
    }
  }
}

:deep(.v-expansion-panel > .v-expansion-panel-title[class*="bg-primary-darken-2"],
      .v-expansion-panel > .v-expansion-panel-title.bg-primary) {
  @include panel-title-hover-fix(".tool-events-header span");
}

:deep(.v-expansion-panel > .v-expansion-panel-title[class*="bg-secondary-darken-2"]) {
  @include panel-title-hover-fix(".event-header .event-message");
  &:hover {
    .event-header .event-badge {
       color: rgb(var(--v-theme-on-surface)) !important;
    }
  }
  &.v-expansion-panel-title--active {
     .event-header .event-badge {
        color: rgb(var(--v-theme-on-surface)) !important;
     }
  }
}

.message-item-wrapper {
  position: relative;
}

.chat-input {
  background-color: rgb(var(--v-theme-background));
}

.flip-container {
  position: relative;
  perspective: 1200px;
  transition: height 0.3s cubic-bezier(0.4, 0.2, 0.2, 1);
}

.flip-card {
  width: 100%;
  position: absolute; 
  top: 0;
  left: 0;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0.2, 0.2, 1);
  background-color: rgb(var(--v-theme-surface));
}

.flip-card .v-card {
  height: 100%; /* Make v-card fill the .flip-card */
  display: flex;
  flex-direction: column;
}

.flip-enter-active,
.flip-leave-active {
  /* Transition is defined on .flip-card directly */
}

.flip-enter-from {
  transform: rotateY(-180deg);
}
.flip-enter-to {
  transform: rotateY(0deg);
}
.flip-leave-from {
  transform: rotateY(0deg);
}
.flip-leave-to {
  transform: rotateY(180deg);
}
</style>
